/** Calendar dates of events ("2026-05-16", no time of day), formatted and compared in local time. */

/** A yyyy-mm-dd date as a local Date at midnight (never shifted by time zones). */
export function localDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

/** Today as yyyy-mm-dd in local time. */
export function todayIso(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** Whole days from today to the date (0 today, negative in the past). */
export function daysUntil(iso: string, now = new Date()): number {
  const today = localDate(todayIso(now));
  return Math.round((localDate(iso).getTime() - today.getTime()) / 86_400_000);
}

/** "16 Mayıs Cumartesi" / "Saturday, May 16". */
export function formatDayLong(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', weekday: 'long' }).format(localDate(iso));
}

/** "16 May" / "May 16". */
export function formatDayShort(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(localDate(iso));
}

/** "8 Mayıs" / "May 8". */
export function formatDayMonth(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }).format(localDate(iso));
}

/** "Cumartesi" / "Saturday". */
export function formatWeekday(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(localDate(iso));
}

/** An event's date or date range: "16 Mayıs Cumartesi" or "9–13 Temmuz". */
export function formatEventDates(startsOn: string, endsOn: string | null, locale: string): string {
  if (!endsOn || endsOn === startsOn) return formatDayLong(startsOn, locale);
  const range = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' });
  // Without spaces around the dash, as the wireframes write ranges ("9–13 Temmuz").
  return range.formatRange(localDate(startsOn), localDate(endsOn)).replace(/\s*–\s*/gu, '–');
}

/** Seconds as "m:ss" (or "h:mm:ss"). */
export function formatLength(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
}

/** "5", "5:00" or "1:05:00" → seconds; null if it is not a length. */
export function parseLength(text: string): number | null {
  const parts = text.trim().split(':');
  if (parts.length === 0 || parts.length > 3 || parts.some((p) => !/^\d+$/.test(p))) return null;
  const numbers = parts.map(Number);
  if (numbers.length === 1) return (numbers[0] ?? 0) * 60;
  if (numbers.slice(1).some((n) => n > 59)) return null;
  return numbers.reduce((total, n) => total * 60 + n, 0);
}
